import type { ReactNode } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import { useState, useLayoutEffect } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { NAME_COLUMN_MIN_WIDTH } from './table-head-custom';

// ----------------------------------------------------------------------

type TableNameCellProps = {
  /** The full text; shown in a tooltip only when the clamp cut it. */
  children: string;
  /** Lines before the clamp; 2 by default. */
  lines?: number;
  /** A muted line under the name (producer · type); never clamped with it. */
  caption?: ReactNode;
  /**
   * A fuller text than `children` (a name shown without its producer). Shown on hover whenever
   * it differs from `children`, clamped or not: it is what the reader cannot see.
   */
  tooltip?: string;
  sx?: SxProps<Theme>;
};

/**
 * NAT-2711 — the content of a name column's cell: at least 240 px, at most `lines` lines, the
 * full text on hover when it was cut. A `<td>` cannot clamp (`-webkit-box` would break table
 * layout), so the content does. Pair it with `kind: 'name'` on the head cell.
 */
export function TableNameCell({ children, lines = 2, caption, tooltip, sx }: TableNameCellProps) {
  // A state-held node (the callback ref): the observer follows the element itself, and the
  // element never changes, because the Tooltip is always rendered.
  const [el, setEl] = useState<HTMLSpanElement | null>(null);
  const [clamped, setClamped] = useState(false);
  // Inside a link or button the ancestor is the tab stop, so the span takes none of its own.
  const [hasInteractiveAncestor, setHasInteractiveAncestor] = useState(false);

  useLayoutEffect(() => {
    if (!el) return;
    setHasInteractiveAncestor(
      !!el.closest(
        [
          'a[href]:not([tabindex="-1"]):not([aria-disabled="true"])',
          'button:not(:disabled):not([tabindex="-1"]):not([aria-disabled="true"])',
          '[role="button"][tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])',
          '[role="link"][tabindex]:not([tabindex="-1"]):not([aria-disabled="true"])',
        ].join(', '),
      ),
    );
  }, [el]);

  useLayoutEffect(() => {
    if (!el) return undefined;
    const measure = () => setClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, children, lines]);

  // A fuller text than the one shown is always worth a hover; otherwise the name, once cut.
  const fuller = tooltip !== undefined && tooltip !== children;
  const tipText = fuller || clamped ? (tooltip ?? children) : '';

  return (
    <Box
      // `normal`: the cell around it is often `nowrap`, which is inherited and would defeat the clamp.
      sx={[
        { minWidth: NAME_COLUMN_MIN_WIDTH, maxWidth: 420, whiteSpace: 'normal' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* Always rendered: swapping it in and out remounts the text and strands the observer. */}
      <Tooltip
        title={tipText}
        placement="top-start"
        // MUI owns hover (with its interactive popper), keyboard focus, touch long-press and Escape.
      >
        <Typography
          ref={setEl}
          // A plain clamped name is a tab stop, and MUI opens the tooltip on this span's
          // :focus-visible. A linked name takes no tab stop of its own: the link is the stop, so
          // its tooltip shows on hover and touch, and the link's accessible name is the full text.
          tabIndex={clamped && !hasInteractiveAncestor ? 0 : undefined}
          variant="body2"
          component="span"
          sx={{
            fontWeight: 'inherit',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: lines,
            overflow: 'hidden',
            overflowWrap: 'anywhere',
          }}
        >
          {children}
        </Typography>
      </Tooltip>
      {caption ? (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {caption}
        </Typography>
      ) : null}
    </Box>
  );
}
